// File: frontend/src/components/User/UserRow.tsx
// Enhanced component with full user management capabilities

import React, { useState } from 'react';
import { 
  Calendar,
  Clock,
  Key,
  Check,
  X,
  User,
  Shield,
  Users,
  Mail,
  MailCheck
} from 'lucide-react';
import { User as UserType } from '@/lib/api/userService';
import { ActionsDropdown, createCommonActions } from '@/components/common/ui';

interface UserRowProps {
  user: UserType;
  onToggleStatus: (id: number) => void;
  onDeleteUser: (id: number) => void;
  onUpdateRole: (id: number, role: string) => void;
  onResetPassword: (id: number) => void;
  isTogglingStatus: boolean;
  isDeletingUser: boolean;
  isUpdatingRole: boolean;
  isResettingPassword: boolean;
  isMobile: boolean;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  onToggleStatus,
  onDeleteUser,
  onUpdateRole,
  onResetPassword,
  isTogglingStatus,
  isDeletingUser,
  isUpdatingRole,
  isResettingPassword,
  isMobile
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return <Shield className="h-4 w-4" />;
      case 'provider':
        return <Users className="h-4 w-4" />;
      case 'customer':
      case 'user':
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'provider':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'provider':
        return 'Provider';
      case 'customer':
      case 'user':
        return 'Customer';
      default:
        return role;
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username;
  };

  const getUserInitials = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`;
    }
    return user.username.charAt(0).toUpperCase();
  };

  const handleRoleChange = (newRole: string) => {
    if (newRole !== user.role) {
      onUpdateRole(user.user_id, newRole);
    }
    setShowRoleDropdown(false);
  };

  const isCurrentlyLoading = isTogglingStatus || isDeletingUser || isUpdatingRole || isResettingPassword;

  if (isMobile) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        {/* User Info */}
        <div className="flex items-start space-x-3">
          <div className="flex items-start space-x-3 flex-1">
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              {user.profile_image ? (
                <img
                  src={user.profile_image}
                  alt={getUserDisplayName()}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-gray-700">
                  {getUserInitials()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {getUserDisplayName()}
              </div>
              <div className="text-sm text-gray-500 truncate flex items-center">
                {user.email}
                {user.email_verified_at && (
                  <MailCheck className="h-4 w-4 ml-1 text-green-500" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status and Role */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
              {getRoleIcon(user.role)}
              <span className="ml-1">{getRoleDisplay(user.role)}</span>
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.is_active)}`}>
              {user.is_active ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Active
                </>
              ) : (
                <>
                  <X className="h-3 w-3 mr-1" />
                  Inactive
                </>
              )}
            </span>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
          <div>
            <span className="font-medium">Last Login:</span>
            <br />
            {formatDateTime(user.last_login)}
          </div>
          <div>
            <span className="font-medium">Created:</span>
            <br />
            {formatDate(user.created_at)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            User ID: {user.user_id}
          </div>
          
          <ActionsDropdown
            size="sm"
            disabled={isCurrentlyLoading}
            actions={[
              createCommonActions.toggleStatus(
                user.is_active,
                () => onToggleStatus(user.user_id),
                isTogglingStatus
              ),
              {
                id: 'reset-password',
                label: 'Reset Password',
                icon: Key,
                onClick: () => onResetPassword(user.user_id),
                variant: 'warning',
                disabled: isResettingPassword
              },
              createCommonActions.delete(
                () => onDeleteUser(user.user_id),
                isDeletingUser
              )
            ]}
          />
        </div>
      </div>
    );
  }

  // Desktop table row
  return (
    <tr className="hover:bg-gray-50">

      {/* User info */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            {user.profile_image ? (
              <img
                src={user.profile_image}
                alt={getUserDisplayName()}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-gray-700">
                {getUserInitials()}
              </span>
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {getUserDisplayName()}
            </div>
            <div className="text-sm text-gray-500 flex items-center">
              {user.email}
              {user.email_verified_at && (
                <MailCheck className="h-4 w-4 ml-1 text-green-500" />
              )}
            </div>
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="relative">
          <button
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            disabled={isCurrentlyLoading}
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)} hover:opacity-80 disabled:opacity-50`}
          >
            {getRoleIcon(user.role)}
            <span className="ml-1">{getRoleDisplay(user.role)}</span>
          </button>
          
          {showRoleDropdown && (
            <div className="absolute z-10 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200">
              <div className="py-1">
                {['admin', 'provider', 'customer'].map((role) => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(role)}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      user.role === role ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      {getRoleIcon(role)}
                      <span className="ml-2">{getRoleDisplay(role)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.is_active)}`}>
          {user.is_active ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Active
            </>
          ) : (
            <>
              <X className="h-3 w-3 mr-1" />
              Inactive
            </>
          )}
        </span>
      </td>

      {/* Email Verified */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {user.email_verified_at ? (
            <>
              <MailCheck className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-700">Verified</span>
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 text-gray-400 mr-1" />
              <span className="text-sm text-gray-500">Unverified</span>
            </>
          )}
        </div>
      </td>

      {/* Last Login */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Clock className="h-4 w-4 mr-1" />
          {formatDateTime(user.last_login)}
        </div>
      </td>

      {/* Created */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-1" />
          {formatDate(user.created_at)}
        </div>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <ActionsDropdown
          size="md"
          disabled={isCurrentlyLoading}
          actions={[
            createCommonActions.toggleStatus(
              user.is_active,
              () => onToggleStatus(user.user_id),
              isTogglingStatus
            ),
            {
              id: 'reset-password',
              label: 'Reset Password',
              icon: Key,
              onClick: () => onResetPassword(user.user_id),
              variant: 'warning',
              disabled: isResettingPassword
            },
            createCommonActions.delete(
              () => onDeleteUser(user.user_id),
              isDeletingUser
            )
          ]}
        />
      </td>
    </tr>
  );
};

export default UserRow;